import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils } from "@/comn/utils";
import { Page, Group, Layout, Button, Grid } from "@/comn/components";
import { useForm, useToast, useFetch, useGrid, usePopup, useResource, usePage } from "@/comn/hooks";
import { URLS, APIS, SG_TANSAD_BLL_LIST } from "./services/OsciBllService"; // 서비스
import { APIS_COMN, SF_COMN_TANSAD_INFO, SF_COMN_TANSAD_SRCH } from "@/tz/go/egaz/osci/colt/comn/OsciColtComnService"; // 공통 서비스
import dayjs from "dayjs";
import { colEnvs, colUtils } from "@/tz/go/egaz/osci/colt/comn";

/*
 * Bill List By TANSAD
 * !== TANSAD 고지 목록 ==!
 */
export const OSCI0401003Q = () => {
    const resource = useResource({
        defaultSchema: [],
    });
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003", "auth_osci0021"],
    }); // Page Unique identifier !== 화면 고유 식별자 ==!
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { tansadNo, scrnId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const popup = usePopup();

    const [tansadBllList, setTansadBllList] = useState(comnUtils.getGridData([]));
    const [vsbContent, setVsbContent] = useState(false);

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0166" },
            { area: "comnCd", comnCd: "OSC0058" },
            { area: "comnCd", comnCd: "OSC0072" },
            { area: "comnCd", comnCd: "OSC0018" },
            { area: "comnCd", comnCd: "OSC0023" },
            { area: "coCd" },
            { area: "bnkCd" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */

    const form = {
        // TANSAD Search !== TANSAD 검색 ==!
        tansadSrch: useForm({
            defaultSchema: SF_COMN_TANSAD_SRCH,
            defaultValues: {},
        }),
        // TANSAD General !== TANSAD 일반 ==!
        tansadGnrl: useForm({
            defaultSchema: SF_COMN_TANSAD_INFO,
            defaultValues: {},
        }),
    };

    const today = new Date();

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // TANSAD Bill List !== TANSAD 고지 목록 ==!
        tansadBllList: useGrid({
            defaultSchema: SG_TANSAD_BLL_LIST,
            page: 0,
            size: 10,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get TANSAD Info !== TANSAD 정보 조회 ==!
        getTansadSrch: useFetch({
            api: (data) =>
                APIS_COMN.getTansad(
                    form.tansadSrch.getValue("cstmOfceCd") +
                        "-" +
                        form.tansadSrch.getValue("tansadYy") +
                        "-" +
                        form.tansadSrch.getValue("tansadSrno"),
                ),
            onSuccess: (data) => {
                const tansadInfo = data.tansadInfo.content;
                if (tansadInfo != null) {
                    setVsbContent(true);

                    const exppnImppnTinNm = tansadInfo.exppnImppnNm
                        ? colUtils.setMask(tansadInfo.exppnImppnTin, "tin") + "(" + tansadInfo.exppnImppnNm + ")"
                        : colUtils.setMask(tansadInfo.exppnImppnTin, "tin");

                    const dcltTinNm = tansadInfo.dcltNm
                        ? colUtils.setMask(tansadInfo.dcltTin, "tin") + "(" + tansadInfo.dcltNm + ")"
                        : colUtils.setMask(tansadInfo.dcltTin, "tin");

                    form.tansadGnrl.setValues({
                        ...tansadInfo,
                        exppnImppnTinNm: exppnImppnTinNm,
                        dcltTinNm: dcltTinNm,
                    });

                    form.tansadSrch.setValues({
                        ...tansadInfo,
                    });
                } else {
                    toast.showToast({ type: "warning", content: "msg.osc.00050" });
                }
            },
            onError: (error) => {
                toast.showToast({ type: "warning", content: "msg.osc.00003" });
            },
            showToast: true,
        }),

        // Get TANSAD Bill List !== TANSAD 고지 목록 조회 ==!
        getBllListByTansad: useFetch({
            api: (data) =>
                APIS.getBllListByTansad(
                    form.tansadSrch.getValue("cstmOfceCd") +
                        "-" +
                        form.tansadSrch.getValue("tansadYy") +
                        "-" +
                        form.tansadSrch.getValue("tansadSrno"),
                    grid.tansadBllList.page,
                    9999,
                ),
            onSuccess: (data: any) => {
                setTansadBllList(comnUtils.getGridData(data.tansadBllList.content));
            },
            onError: () => {},
            showToast: true,
        }),
        // Get TANSAD Detail !== TANSAD 상세 조회 ==!
        getTansad: useFetch({
            api: (data) => APIS_COMN.getTansad(tansadNo),
            enabled: !!tansadNo,
            onSuccess: (data) => {
                const tansadInfo = data.tansadInfo.content;
                if (tansadInfo != null) {
                    setVsbContent(true);

                    const exppnImppnTinNm = tansadInfo.exppnImppnNm
                        ? colUtils.setMask(tansadInfo.exppnImppnTin, "tin") + "(" + tansadInfo.exppnImppnNm + ")"
                        : colUtils.setMask(tansadInfo.exppnImppnTin, "tin");

                    const dcltTinNm = tansadInfo.dcltNm
                        ? colUtils.setMask(tansadInfo.dcltTin, "tin") + "(" + tansadInfo.dcltNm + ")"
                        : colUtils.setMask(tansadInfo.dcltTin, "tin");
                    form.tansadGnrl.setValues({
                        ...tansadInfo,
                        exppnImppnTinNm: exppnImppnTinNm,
                        dcltTinNm: dcltTinNm,
                    });

                    form.tansadSrch.setValues({
                        ...tansadInfo,
                    });

                    fetch.getBllListByTansad.fetch();
                } else {
                    toast.showToast({ type: "warning", content: "msg.osc.00050" });
                }
            },
            onError: (error) => {
                toast.showToast({ type: "warning", content: "msg.osc.00003" });
            },
            showToast: true,
        }),
    };
    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Get TANSAD Info !== TANSAD 정보 조회 ==!
        getTansadSrch: () => {
            form.tansadSrch.handleSubmit((data) => {
                // Validation Check !== 밸리데이션 체크 ==!
                fetch.getTansadSrch.fetch();
                fetch.getBllListByTansad.fetch();
            })();
        },
    };

    const render = {
        grid_tansadBllList: {
            head: {},
            cell: {
                bllNo: (props: any) => {
                    const { binding, rowValues, value } = props;
                    return (
                        <Layout direction="col" gap={1}>
                            <span style={{ color: "blue" }}>
                                <Link
                                    to={`${URLS.osci0401002q}/${rowValues.bllTpCd}-${rowValues.bllSysmCd}-${rowValues.bllYy}-${rowValues.bllSrno}-${rowValues.bllDgr} `}
                                >{`${rowValues.bllTpCd}-${rowValues.bllSysmCd}-${rowValues.bllYy}-${rowValues.bllSrno}-${rowValues.bllDgr}`}</Link>
                            </span>
                        </Layout>
                    );
                },
            },
            edit: {},
        },
    };

    useEffect(() => {
        form.tansadSrch.setEditable(true);
        form.tansadGnrl.setEditable(false);
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Label {...form.tansadSrch.schema.tansadNo}></Group.Label>
                            <Group.Field size={4}>
                                <Group.Control
                                    {...form.tansadSrch.schema.cstmOfceCd}
                                    onPaste={(e: any) => {
                                        e.preventDefault();
                                        const data = e.clipboardData.getData("Text").replaceAll("-", "");

                                        form.tansadSrch.setValue("cstmOfceCd", data.substring(0, 4));
                                        form.tansadSrch.setValue("tansadYy", data.substring(4, 6));
                                        form.tansadSrch.setValue("tansadSrno", data.substring(6, 13));
                                    }}
                                />
                                <Group.Any>-</Group.Any>
                                <Group.Control {...form.tansadSrch.schema.tansadYy} />
                                <Group.Any>-</Group.Any>
                                <Group.Control {...form.tansadSrch.schema.tansadSrno} />
                            </Group.Field>
                        </Group.Row>
                    </Group.Section>
                    <Layout direction="row">
                        <Layout.Right>
                            <Button
                                role="search"
                                onClick={() => {
                                    handler.getTansadSrch();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            {vsbContent && (
                <Group>
                    <Group.Body>
                        <Group.Title title={"L_TANSAD_GNRL"} titleSize={2}></Group.Title>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.tansadGnrl.schema.tansadNo} />
                                <Group.Control {...form.tansadGnrl.schema.tansadDt} />
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.tansadGnrl.schema.regm} />
                                <Group.Control {...form.tansadGnrl.schema.csclPlnCd} />
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.tansadGnrl.schema.exppnImppnTinNm} />
                                <Group.Control {...form.tansadGnrl.schema.dcltTinNm} />
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.tansadGnrl.schema.dclrPrcssSttsCd} />
                            </Group.Row>
                        </Group.Section>
                    </Group.Body>
                </Group>
            )}
            {vsbContent && (
                <Group>
                    <Group.Body>
                        <Group.Title title={"L_BLL_LST"} titleSize={2}></Group.Title>
                        <Layout direction="row">
                            <Layout.Right>
                                <Button
                                    role="excelDown"
                                    onClick={() => {
                                        grid.tansadBllList.exportExcel({
                                            /* 다운로드 엑셀 파일명 (확장자 없이), 기본값 Export(년월일시분초) */
                                            excelName: dayjs().format("YYYY-MM-DD HH:mm:ss") + "_" + pgeUid,
                                            /* 다운로드 엑셀 시트명 , 기본값 Sheet */
                                            sheetName: "Sheet Name",
                                        });
                                    }}
                                ></Button>
                            </Layout.Right>
                        </Layout>
                        <Grid {...grid.tansadBllList.grid} data={tansadBllList} render={render.grid_tansadBllList} />
                    </Group.Body>
                </Group>
            )}
            {!comnUtils.isPopup() && (
                <Layout direction="row">
                    <Layout.Left>
                        <Button
                            role="list"
                            onClick={() => {
                                if (scrnId == "OSCI0402001Q") {
                                    navigate(URLS.osci0402001q);
                                } else if (scrnId == "OSCI0402003Q") {
                                    navigate(URLS.osci0402003q);
                                } else if (scrnId == "OSCI0402004Q") {
                                    navigate(URLS.osci0402004q);
                                } else {
                                    navigate(URLS.osci0401001q);
                                }
                            }}
                        ></Button>
                    </Layout.Left>
                </Layout>
            )}
            {comnUtils.isPopup() && (
                <Layout direction="row">
                    <Layout.Right>
                        <Button
                            role="close"
                            onClick={() => {
                                popup.close();
                            }}
                        />
                    </Layout.Right>
                </Layout>
            )}
        </Page>
    );
};
